import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { forwardRef, useState, useMemo, useEffect, useRef } from 'react';
import classNames from 'classnames';
import inputClasses from './Input2.js';
import FormFieldWrapper from '../Form/FormFields/FormFieldWrapper.js';
import useFormField from '../Form/useFormField.js';
import '../../hooks/index.js';
import '../../hooks/useId/index.js';
import '../../utils/mergeRefs/index.js';
import useId from '../../hooks/useId/useId.js';
import useSSRLayoutEffect from '../../hooks/useSSRLayoutEffect/useSSRLayoutEffect.js';
import mergeRefs from '../../utils/mergeRefs/mergeRefs.js';

const getAddOnComponent = (addOn, containerClassName, containerRef) => {
  if (!addOn) {
    return null;
  }
  const isAddOnAStringOrNumber = typeof addOn === 'string' || typeof addOn === 'number';
  return /*#__PURE__*/React__default.createElement("span", {
    className: classNames(containerClassName, {
      [inputClasses.addOnTextNode]: isAddOnAStringOrNumber
    }),
    "aria-hidden": isAddOnAStringOrNumber,
    ref: containerRef
  }, addOn);
};
const Input = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _ref2;
  let {
    name: externalName,
    id,
    type = 'text',
    initialValue,
    value: externalValue,
    onChange: externalOnChange,
    onBlur: externalOnBlur,
    label,
    leftAddOn,
    rightAddOn,
    errorText,
    helperText,
    className: externalClassName,
    'data-testid': dataTestId,
    style,
    allowDecimalNumbers = true,
    min,
    max,
    ...rest
  } = _ref;
  const {
    register,
    fieldErrors
  } = useFormField();
  const inputId = useId(id);
  const [internalValue, setInternalValue] = useState(initialValue !== null && initialValue !== void 0 ? initialValue : '');
  const inputRegex = /^(?!-?\d+$).+$/;
  const minValue = useMemo(() => min !== undefined && min !== null ? Number(min) : undefined, [min]);
  const maxValue = useMemo(() => max !== undefined && max !== null ? Number(max) : undefined, [max]);
  const registerProps = externalName && register ? register(externalName, {
    type: 'text'
  }) : undefined;
  useEffect(() => {
    if (externalValue !== undefined || registerProps !== undefined || allowDecimalNumbers) {
      setInternalValue(undefined);
    }
  }, [allowDecimalNumbers, externalValue, registerProps]);
  const onChange = e => {
    if (!allowDecimalNumbers && e.target.value !== '-' && inputRegex.test(e.target.value)) {
      return;
    }
    if (internalValue !== undefined) {
      setInternalValue(e.target.value);
    }
    externalOnChange === null || externalOnChange === void 0 || externalOnChange(e);
    registerProps === null || registerProps === void 0 || registerProps.onChange(e);
  };
  const onBlur = e => {
    if (!allowDecimalNumbers && e.target.value !== '') {
      if (e.target.value === '-' && internalValue !== undefined) {
        setInternalValue('');
      } else {
        const currentValue = Math.max(minValue || Number.MIN_SAFE_INTEGER, Math.min(Number(e.target.value), maxValue || Number.MAX_SAFE_INTEGER));
        if (internalValue !== undefined) {
          setInternalValue(currentValue.toString());
        }
      }
    }
    externalOnBlur === null || externalOnBlur === void 0 || externalOnBlur(e);
    registerProps === null || registerProps === void 0 || registerProps.onBlur(e);
  };
  const leftAddOnRef = useRef(null);
  const rightAddOnRef = useRef(null);
  const [addOnWidths, setAddOnWidths] = useState({
    leftAddOnWidth: 0,
    rightAddOnWidth: 0
  });
  const ADD_ON_HORIZONTAL_PADDING = 12;
  useSSRLayoutEffect(() => {
    let leftAddOnWidth = 0;
    let rightAddOnWidth = 0;
    if (leftAddOnRef.current) {
      leftAddOnWidth = leftAddOnRef.current.getBoundingClientRect().width;
    }
    if (rightAddOnRef.current) {
      rightAddOnWidth = rightAddOnRef.current.getBoundingClientRect().width;
    }
    setAddOnWidths({
      leftAddOnWidth,
      rightAddOnWidth
    });
  }, [leftAddOn,
  // Re-runs effect when the left add-on changes
  rightAddOn // Re-runs effect when the right add-on changes
  ]);
  return /*#__PURE__*/React__default.createElement(FormFieldWrapper, {
    inputId: inputId,
    "data-testid": dataTestId,
    label: label,
    errorText: errorText !== null && errorText !== void 0 ? errorText : externalName && (fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors[externalName]),
    helperText: helperText,
    className: externalClassName,
    style: style
  }, /*#__PURE__*/React__default.createElement("span", {
    className: inputClasses.container
  }, getAddOnComponent(leftAddOn, inputClasses.leftAddOn, leftAddOnRef), /*#__PURE__*/React__default.createElement("input", _extends({}, rest, {
    id: inputId,
    name: externalName !== null && externalName !== void 0 ? externalName : registerProps === null || registerProps === void 0 ? void 0 : registerProps.name,
    type: allowDecimalNumbers ? type : 'text',
    value: (_ref2 = externalValue !== null && externalValue !== void 0 ? externalValue : registerProps === null || registerProps === void 0 ? void 0 : registerProps.value) !== null && _ref2 !== void 0 ? _ref2 : internalValue,
    onChange: onChange,
    onBlur: onBlur,
    className: inputClasses.input,
    style: {
      paddingLeft: "".concat(addOnWidths.leftAddOnWidth + ADD_ON_HORIZONTAL_PADDING, "px"),
      paddingRight: "".concat(addOnWidths.rightAddOnWidth + ADD_ON_HORIZONTAL_PADDING, "px")
    },
    "data-testid": dataTestId,
    ref: mergeRefs(ref, registerProps === null || registerProps === void 0 ? void 0 : registerProps.ref)
  })), getAddOnComponent(rightAddOn, inputClasses.rightAddOn, rightAddOnRef)));
});

export { Input as default };
